interface ProductMapping {
  packageDI: string
  description: string
  modelRef: string
  udiDI: string
  quantity: number
}

// CSV data for Stryker 2.0 MP Plates Tray
const STRYKER_PRODUCT_MAPPINGS: ProductMapping[] = [
  {
    packageDI: '34546540602542',
    description: '2.0x4mm Bone screw, Self-tapping, MP, Cross-pin',
    modelRef: '50-20704',
    udiDI: '04546540602541',
    quantity: 20,
  },
  {
    packageDI: '34546540602559',
    description: '2.0x5mm Bone screw, Self-tapping, MP, Cross-pin',
    modelRef: '50-20705',
    udiDI: '04546540602558',
    quantity: 40,
  },
  {
    packageDI: '34546540602566',
    description: '2.0x6mm Bone screw, Self-tapping, MP, Cross-pin',
    modelRef: '50-20706',
    udiDI: '04546540602565',
    quantity: 40,
  },
  {
    packageDI: '34546540602573',
    description: '2.0x8mm Bone screw, Self-tapping, MP, Cross-pin',
    modelRef: '50-20708',
    udiDI: '04546540602572',
    quantity: 20,
  },
  {
    packageDI: '34546540602580',
    description: '2.0x10mm Bone screw, Self-tapping, MP, Cross-pin',
    modelRef: '50-20710',
    udiDI: '04546540602589',
    quantity: 20,
  },
  {
    packageDI: '34546540602597',
    description: '2.0x12mm Bone screw, Self-tapping, MP, Cross-pin',
    modelRef: '50-20712',
    udiDI: '04546540602596',
    quantity: 20,
  },
  {
    packageDI: '34546540602603',
    description: '2.0x14mm Bone screw, Self-tapping, MP, Cross-pin',
    modelRef: '50-20714',
    udiDI: '04546540602602',
    quantity: 10,
  },
  {
    packageDI: '34546540602979',
    description: '2.0x4mm Screw (bone), Self-tapping, MP, Cross-pin',
    modelRef: '50-20904',
    udiDI: '04546540602978',
    quantity: 30,
  },
  {
    packageDI: '34546540602986',
    description: '2.0x5mm Screw (bone), Self-drilling, MP, Cross-pin',
    modelRef: '50-20905',
    udiDI: '04546540602985',
    quantity: 50,
  },
  {
    packageDI: '34546540602993',
    description: '2.0x6mm Screw (bone), Self-drilling, MP, Cross-pin',
    modelRef: '50-20906',
    udiDI: '04546540602992',
    quantity: 20,
  },
  {
    packageDI: '34546540602641',
    description: '2.3x6mm Screw (bone), Self-drilling, MP, Cross-pin',
    modelRef: '50-23706',
    udiDI: '04546540602640',
    quantity: 10,
  },
  {
    packageDI: '37613154184768',
    description: '2.3x8mm Screw (bone), Emergency, MP, Cross-pin',
    modelRef: '50-23708',
    udiDI: '07613154184767',
    quantity: 10,
  },
  {
    packageDI: '37613154184775',
    description: '2.3x10mm Screw (bone), Emergency, MP, Cross-pin',
    modelRef: '50-23710',
    udiDI: '07613154184774',
    quantity: 10,
  },
  {
    packageDI: '37613154184782',
    description: '2.3x12mm Screw (bone), Emergency, MP, Cross-pin',
    modelRef: '50-23712',
    udiDI: '07613154184781',
    quantity: 10,
  },
  {
    packageDI: '37613154184799',
    description: '2.3x14mm Screw (bone), Emergency, MP, Cross-pin',
    modelRef: '50-23714',
    udiDI: '07613154184798',
    quantity: 5,
  },
  {
    packageDI: '37613154184805',
    description: '2.3x16mm Screw (bone), Emergency, MP, Cross-pin',
    modelRef: '50-23716',
    udiDI: '07613154184804',
    quantity: 5,
  },
]

// Create lookup maps for efficient searching
const packageDILookup = new Map<string, ProductMapping>()
const udiDILookup = new Map<string, ProductMapping>()

// Initialize lookup maps
STRYKER_PRODUCT_MAPPINGS.forEach((mapping) => {
  packageDILookup.set(mapping.packageDI, mapping)
  udiDILookup.set(mapping.udiDI, mapping)
})

/**
 * Extract screw size from description
 * @param description - Full product description
 * @returns Screw size (e.g., "2.0x4mm") or null if not found
 */
export const extractScrewSize = (description: string): string | null => {
  // Match patterns like "2.0x4mm", "2.3x10mm", etc.
  const sizeMatch = description.match(/(\d+\.\d+x\d+mm)/i)
  return sizeMatch ? sizeMatch[1] : null
}

/**
 * Get product description by Package DI
 * @param packageDI - Package Device Identifier
 * @returns Full product description or null if not found
 */
export const getProductDescriptionByPackageDI = (
  packageDI: string
): string | null => {
  const mapping = packageDILookup.get(packageDI)
  return mapping ? mapping.description : null
}

/**
 * Get product description by UDI-DI
 * @param udiDI - UDI Device Identifier
 * @returns Full product description or null if not found
 */
export const getProductDescriptionByUDIDI = (udiDI: string): string | null => {
  const mapping = udiDILookup.get(udiDI)
  return mapping ? mapping.description : null
}

/**
 * Get screw size by Package DI
 * @param packageDI - Package Device Identifier
 * @returns Screw size or null if not found
 */
export const getScrewSizeByPackageDI = (packageDI: string): string | null => {
  const description = getProductDescriptionByPackageDI(packageDI)
  return description ? extractScrewSize(description) : null
}

/**
 * Get screw size by UDI-DI
 * @param udiDI - UDI Device Identifier
 * @returns Screw size or null if not found
 */
export const getScrewSizeByUDIDI = (udiDI: string): string | null => {
  const description = getProductDescriptionByUDIDI(udiDI)
  return description ? extractScrewSize(description) : null
}

/**
 * Attempt to get screw size from various identifiers
 * @param deviceId - Device ID from scanned product
 * @param udi - UDI from scanned product
 * @returns Screw size or null if not found
 */
export const getScrewSizeFromProduct = (
  deviceId?: string,
  udi?: string
): string | null => {
  // Try to find by device ID first (assuming it might be a Package DI)
  if (deviceId) {
    const sizeByDeviceId = getScrewSizeByPackageDI(deviceId)
    if (sizeByDeviceId) return sizeByDeviceId

    // Try by UDI-DI
    const sizeByUDI = getScrewSizeByUDIDI(deviceId)
    if (sizeByUDI) return sizeByUDI
  }

  // Try to find by UDI
  if (udi) {
    const sizeByUDI = getScrewSizeByUDIDI(udi)
    if (sizeByUDI) return sizeByUDI

    // Try by Package DI
    const sizeByPackageDI = getScrewSizeByPackageDI(udi)
    if (sizeByPackageDI) return sizeByPackageDI
  }

  return null
}
